import { configure, defineRule } from 'vee-validate';
import {
  required,
  email,
  min,
  max,
  min_value,
  max_value,
  numeric,
  confirmed,
  alpha_spaces,
  digits,
  integer
} from '@vee-validate/rules';
import { localize, setLocale } from '@vee-validate/i18n';
import ru from '@vee-validate/i18n/dist/locale/ru.json';
import declOfNum from './declension';

defineRule('required', required);
defineRule('email', email);
defineRule('min', min);
defineRule('max', max);
defineRule('min_value', min_value);
defineRule('max_value', max_value);
defineRule('numeric', numeric);
defineRule('confirmed', confirmed);
defineRule('alpha_spaces', alpha_spaces);
defineRule('digits', digits);
defineRule('integer', integer);

const PHONE_RE = /^\+?(7|8)[- _]*\(?[- _]*(\d{3}[- _]*\)?([- _]*\d){7}|\d\d[- _]*\d\d[- _]*\)?([- _]*\d){6})$/;

defineRule('phone', (value: unknown) => {
  if (value == null || value === '') {
    return true;
  }

  return PHONE_RE.test(String(value)) || 'Не является мобильным номером';
});

defineRule('phoneOrEmail', (value: unknown) => {
  if (value == null || value === '') {
    return true;
  }

  const normalized = String(value);
  const isPhone = PHONE_RE.test(normalized);
  const isEmail = /.+@.+\..+/.test(normalized);

  return isPhone || isEmail || 'Пожалуйста, введите корректные данные.';
});

type RuleCtx = {
  rule: {
    params: unknown[];
  };
};

configure({
  bails: false,
  // vee-validate i18n dictionary typing omits custom message fn shapes
  generateMessage: localize({
    ru: {
      ...ru,
      messages: {
        ...ru.messages,
        required: 'Пожалуйста, заполните это поле',
        email: 'Неверный формат',
        numeric: 'Пожалуйста, укажите в этом поле только цифры',
        alpha_spaces: 'Поле должно содержать только буквы',
        confirmed: 'Пароли должны совпадать',
        min_value: ({ rule }: RuleCtx) => `Пожалуйста, укажите значение не меньше ${rule.params[0]}`,
        max_value: ({ rule }: RuleCtx) => `Пожалуйста, укажите значение не больше ${rule.params[0]}`,
        digits: ({ rule }: RuleCtx) => {
          const count = Number(rule.params[0]);
          return `Пожалуйста, укажите ровно ${count} ${declOfNum(count, ['цифру', 'цифры', 'цифр'])}`;
        },
        min: ({ rule }: RuleCtx) => {
          const count = Number(rule.params[0]);
          return `Пожалуйста, укажите не менее ${count} ${declOfNum(count, ['символ', 'символа', 'символов'])}`;
        },
        max: ({ rule }: RuleCtx) => {
          const count = Number(rule.params[0]);
          return `Пожалуйста, укажите не более ${count} ${declOfNum(count, ['символ', 'символа', 'символов'])}`;
        }
      },
      fields: {
        email: {
          required: 'E-Mail не может быть пустым',
          email: 'Неверный формат'
        },
        name: {
          alpha_spaces: 'Поле должно содержать только буквы'
        },
        partName: {
          required: 'Пожалуйста, укажите название или каталожный номер'
        },
        phone: {
          required: 'Телефон не может быть пустым',
          phone: 'Не корректный номер телефона'
        },
        phoneValidate: {
          required: 'Пожалуйста, введите номер телефона'
        },
        password: {
          confirmed: 'Пароли должны совпадать',
          min: 'Минимальная длина пароля 8 символов'
        },
        code: {
          required: 'Пожалуйста, введите код из SMS'
        },
        file: {
          required: 'Пожалуйста, прикрепите файл допустимого формата.'
        }
      }
    }
  } as never)
});

setLocale('ru');
